/**
 * English resources — the source of truth for the key structure.
 *
 * `de.ts` is typed against this object, so a missing or misspelled key is a
 * compile error rather than an `nav.instances` string showing up in the UI.
 * Add keys here first.
 */
const en = {
  common: {
    optionalParens: '(optional)',
    dashOptional: '— optional',
    unsaved: 'Not saved',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    add: 'Add',
    back: 'Back',
    search: 'Search',
    loading: 'Loading…',
    saving: 'Saving…',
    none: '—',
    optional: 'optional',
    required: 'required',
    yes: 'Yes',
    no: 'No',
    actions: 'Actions',
    status: 'Status',
    name: 'Name',
    email: 'E-mail',
    role: 'Role',
    notes: 'Notes',
    createdAt: 'Created',
    updatedAt: 'Updated',
    never: 'never',
    show: 'Show',
    hide: 'Hide',
    copy: 'Copy',
    copied: 'Copied',
    download: 'Download',
    refresh: 'Refresh',
    retry: 'Retry',
    confirm: 'Confirm',
    unknownError: 'Something went wrong. Please try again.',
    noResults: 'Nothing found.',
    of: 'of',
    page: 'Page',
    perPage: 'per page',
    all: 'All',
    language: 'Language',
  },

  nav: {
    sectionOverview: 'Overview',
    sectionBusiness: 'Business',
    sectionOperations: 'Operations',
    dashboard: 'Dashboard',
    monitoring: 'Monitoring',
    bereitschaft: 'On-call',
    gitRegistry: 'Git & registry',
    analytics: 'Analytics',
    sicherungsziele: 'Backup targets',
    customers: 'Customers',
    orders: 'Orders',
    products: 'Products',
    applications: 'Applications',
    instances: 'Instances',
    apps: 'Shops and apps',
    webseiten: 'Websites',
    fmMigration: 'FileMaker migration',
    servers: 'Servers',
    inference: 'AI Gateway',
    verbund: 'Publisher network',
    domains: 'Domains',
    deployments: 'Deployments',
    releases: 'Releases',
    feedback: 'Feedback inbox',
    subtitle: 'Provisioning Center',
  },

  eyebrow: {
    pcSettings: 'PC settings',
    platformAdministration: 'Platform administration',
    users: 'Users',
    usersDescription: 'Staff, roles, suspensions',
    integrations: 'Integrations',
    integrationsDescription: 'Cloud providers, WooCommerce, management addresses',
    apiKeys: 'API keys',
    apiKeysDescription: 'Access for CLI, MCP and automation',
    embedKeys: 'Embed keys',
    embedKeysDescription: 'Keys for the pricing widgets',
    baseStacks: 'Base stacks',
    baseStacksDescription: 'Shared infrastructure per host — proxy, sync, monitoring',
    auditLog: 'Audit log',
    auditLogDescription: 'Who changed what, and when',
    changePassword: 'Change password',
    twoFactor: 'Two-factor authentication',
    registryCredential: 'My registry access',
    sshKeys: 'My SSH access',
    signOut: 'Sign out',
  },

  roles: {
    ADMIN: 'Administrator',
    OPERATIONS: 'Operations',
    PRODUCT_MANAGER: 'Product management',
    SUPPORT: 'Technical support',
    SALES: 'Sales',
    BILLING: 'Billing',
  },

  noAccess: {
    title: 'No access to this area',
    restrictedTo: 'This page is restricted to {{roles}}.',
    yourRole: 'Your account has the role {{role}}.',
    askAdmin:
      'If you need access, an administrator can change your role under PC settings › Users.',
  },

  tables: {
    search: 'Search…',
    filterAll: 'all',
    clearFilters: '{{shown}} of {{total}} — clear filters',
    range: '{{von}}–{{bis}} of {{gesamt}}',
    perPage: 'Rows per page',
    perPageOption: '{{n}} per page',
    prevPage: 'Previous page',
    nextPage: 'Next page',
    noMatch: 'Nothing matches the search and filters.',
    created: 'Created',
    provider: 'Provider',
    exceptDecommissioned: 'everything except decommissioned',
    shared: 'Shared',
    dedicated: 'Dedicated',
    customerOwned: 'Customer-owned',
    searchServers: 'Hostname, IP, provider…',
    customer: 'Customer',
    product: 'Product',
    plan: 'Plan',
    domain: 'Domain',
    server: 'Server',
    serverHosting: 'Server / hosting',
    deployed: 'Deployed',
    type: 'Type',
    instance: 'Instance',
    duration: 'Duration',
    retries: 'Retries',
    timestamp: 'Timestamp',
    user: 'User',
    action: 'Action',
    entityType: 'Entity type',
    entityId: 'Entity ID',
    ip: 'IP',
    automated: 'Automated',
    prefix: 'Prefix',
    lastUsed: 'Last used',
    wcOrder: 'WC order',
    payment: 'Payment',
    paid: 'Paid',
    hostname: 'Hostname',
    sshKey: 'SSH key',
    slots: 'Slots',
    hardware: 'CPU / RAM / disk',
    assignment: 'Assignment',
  },

  filters: {
    allStatusesEn: 'All statuses',
    allStatuses: 'All statuses',
    allTypes: 'All types',
    allProducts: 'All products',
    allEntityTypes: 'All entity types',
    searchDomain: 'Search by domain…',
    filterAction: 'Filter by action…',
  },

  empty: {
    deployments: 'No deployment jobs yet.',
    instances: 'No instances yet.',
    orders: 'No orders yet.',
    audit: 'No audit entries yet.',
    customerUsers: 'No portal accounts yet.',
    servers: 'No servers yet. Use “Add server”.',
  },

  dashboard: {
    recentDeployments: 'Recent deployment jobs',
  },

  products: {
    archive: "Archive",
    archiveConfirm: "Archive product \"{{name}}\"?\n\nIt disappears from the offering; everything running stays untouched.",
    deleteConfirm: "Delete product \"{{name}}\" for good?\n\nNo instance runs on it. Its plans, compose templates and releases go with it. Orders stay, but lose their product reference.",
    notFound: 'Product not found.',
    notSynced: 'not yet synced',
    noPlans: 'No plans yet.',
    noTemplates: 'No templates yet.',
    showTemplate: 'Show compose template',
    editProduct: 'Edit product',
    syncHint: 'These fields sync straight to the WooCommerce product.',
    imageUrl: 'Image URL',
    optionsCommaSeparated: 'Options (comma-separated)',
    addTemplate: 'Add compose template',
    composeTemplate: 'Compose template (YAML)',
    envTemplate: 'Env template',
    newProduct: 'New product',
  },

  apiKeys: {
    shownOnce: 'This key is shown only now — it cannot be retrieved afterwards. Copy it now.',
    nameExample: 'e.g. Claude Agent, CI pipeline',
    forCliAs: 'For CLI/MCP as',
    orAsBearer: 'or directly as a bearer token:',
    nameLabel: 'Name *',
    namePlaceholder: 'e.g. Claude Agent, CI pipeline',
    cliHintBefore: 'For CLI/MCP set',
    cliHintAfter: 'or pass it directly as a bearer token:',
  },

  help: {
    buttonTitle: 'Help for this area',
  },

  registry: {
    refreshHint: 'Clear the cache and fetch the catalogue again',
    myAccount: 'My registry account',
    chooseFromRegistry: 'Choose from registry',
    enterManually: 'Enter manually',
    loadingRepositories: 'Loading repositories…',
    selectRepository: '— select repository —',
    selectRepositoryFirst: 'Select a repository first',
    loadingTags: 'Loading tags…',
    tagsUnavailable: 'Tags unavailable',
    selectTag: '— select tag —',
    notInCatalogOption: '{{repo}} (not in catalog)',
    tagGoneOption: '{{tag}} (no longer present)',
    unreachable: 'Registry unreachable{{detail}} — please enter manually.',
    notConfigured:
      'No registry configured. Set one up under PC settings › Integrations, then images can be picked here.',
    notInCatalogHint:
      '“{{repo}}” is not in the registry catalog — probably a third-party image, or deleted.',
    tagPlaceholder: 'Tag, e.g. 20260803-1929',
  },

  auth: {
    onlyNow: 'only now',
    passwordSet: 'Password set',
    redirectingToLogin: 'Taking you to the sign-in page…',
    invalidLink: 'Invalid link',
    setPassword: 'Set a password',
    qrForSetup: 'QR code for setup',
    noScanner: 'No scanner? Enter the key by hand',
    emailSent: 'E-mail sent',
    minEightChars: 'at least 8 characters',
    confirmNewPassword: 'Confirm the new password',
    signIn: 'Sign in',
    signInSubtitle: 'Sign in to your account',
    verifying: 'Checking…',
    mfaCodeLabel: 'Code from your authenticator app',
    signingIn: 'Signing in…',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    backToLogin: 'Back to sign in',
    invalidCredentials: 'E-mail or password is incorrect.',
    accountInactive: 'This account is not active. Please contact an administrator.',
    resetTitle: 'Reset password',
    resetIntro:
      'Enter your e-mail address and we will send you a link to choose a new password.',
    resetSubmit: 'Send link',
    resetSent:
      'If an account exists for this address, a link is on its way. Please also check your spam folder.',
    newPasswordTitle: 'Choose a new password',
    newPassword: 'New password',
    repeatPassword: 'Repeat password',
    passwordsDoNotMatch: 'The passwords do not match.',
    passwordTooShort: 'The password must be at least {{count}} characters long.',
    passwordChanged: 'Password changed. You can sign in now.',
    tokenInvalid: 'This link is invalid or has expired. Please request a new one.',
    currentPassword: 'Current password',
    changePasswordTitle: 'Change password',
    changePasswordSuccess: 'Password changed.',
    mfaTitle: 'Two-factor authentication',
    mfaEnterCode: 'Enter the six-digit code from your authenticator app.',
    mfaCode: 'Code',
    mfaVerify: 'Verify',
    mfaInvalidCode: 'This code is not valid. Please try again.',
    mfaBackupHint: 'You can also use one of your backup codes.',
    mfaSetupTitle: 'Set up two-factor authentication',
    mfaSetupScan: 'Scan this QR code with Google Authenticator, Microsoft Authenticator or 1Password.',
    mfaSetupManual: 'Cannot scan? Enter this key manually:',
    mfaSetupConfirm: 'Confirm with a code from the app to finish.',
    mfaEnabled: 'Two-factor authentication is active.',
    mfaDisabled: 'Two-factor authentication is off.',
    mfaEnable: 'Enable',
    mfaDisable: 'Disable',
    mfaBackupCodesTitle: 'Backup codes',
    mfaBackupCodesIntro:
      'Keep these somewhere safe. Each one works once and lets you sign in if your phone is unavailable. They are shown only now.',
    mfaBackupCodesRemaining: '{{count}} backup codes left',
    mfaDisableConfirm:
      'Turn off two-factor authentication? Your account will then be protected by the password alone.',
  },

  forms: {
    slugRequired: 'Slug *',
    productRequired: 'Product *',
    selectProduct: 'Select product…',
    versionRequired: 'Version *',
    chooseCustomer: '— choose customer —',
    optionalRecommended: '— optional, but recommended',
    ifKeyProtected: '(if the key is protected with one)',
    emptyIfNoPassword: 'Leave empty if there is none',
    privateSshKey: 'Private SSH key (PEM)',
    nameRequired: 'Name *',
    exampleNaboo: 'e.g. naboo',
    sizeRequired: 'Size *',
    diskGb: 'Disk (GB)',
    gp3Note: 'gp3, deleted together with the instance.',
    exampleHoth: 'e.g. hoth',
    serverTypeRequired: 'Server type *',
    imageRequired: 'Image *',
    noImageLookup: 'No lookup available — e.g. ubuntu-24.04, debian-12',
    vpsPlanRequired: 'VPS plan (catalog item) *',
    osTemplateRequired: 'Template (OS) *',
    dataCenterRequired: 'Data centre *',
    publicKey: 'Public key',
    privateKeyPem: 'Private key (PEM)',
    passphraseOptional: 'Passphrase (optional)',
    maxSlots: 'Max. slots',
    hostnameRequired: 'Hostname *',
    ipRequired: 'IP address *',
    initialStatus: 'Initial status',
    statusReady: 'READY — ready for deployments',
    statusActive: 'ACTIVE — in service',
    statusProvisioning: 'PROVISIONING — being set up',
    statusMaintenance: 'MAINTENANCE — maintenance mode',
    exampleRegion: 'e.g. nbg1, eu-west',
    diskGbPlain: 'Disk (GB)',
    exampleGpu: 'e.g. NVIDIA L4',
    costPerMonth: 'Cost per month (€)',
  },

  servers: {
    replaceKey: "Replace",
    storeKey: "Store a key",
    removeKey: "Remove access",
    removeKeyConfirm: "Remove our SSH key for \"{{host}}\"?\n\nThe machine stays as it is; only the Provisioning Center loses its access. Deployments and host checks stop working from here.",
    hostCheck: 'Host check',
    checkNotPossible: 'Check not possible',
    rolloutBaseStacks: 'Roll out base stacks',
    hostCheckHint: 'Does this host meet every condition for a deployment?',
    baseStackHint: 'Roll out or inspect the shared infrastructure on this host',
  },

  activation: {
    newState: 'New state',
    stateActive: 'Active — lift every restriction',
    stateBlocked: 'Blocked — starts the escalation',
    stateTerminated: 'Contract ended — escalation, final step',
    reasonExample: 'e.g. invoice 2026-0412 unpaid for 45 days, third reminder sent',
    noticeTitleExample: 'Title, e.g. \"Access restricted — unpaid invoice\"',
    noticeBodyHint:
      'What to do, by when, who to contact. Please mention that exporting data is still possible.',
    lastContact: 'Last contact',
    reportedVersion: 'Reported version',
    tokenValidUntil: 'Token valid until',
    stateAccordingToInstance: 'State according to the instance',
    recipients: 'Recipients',
    adminsOnly: 'Administrators of the instance only',
    allUsers: 'All users',
    messageBody: 'Message text',
    noActiveNotices: 'No active messages.',
    systemGenerated: 'Generated by the system from the payment state',
    withdrawNotice: 'Withdraw the message (kept on record for traceability)',
    activationKey: 'Activation key',
  },

  embed: {
    copyToClipboard: 'Copy to clipboard',
    newKey: 'Create a new embed key',
    exampleName: 'Marketing juni.com',
    noRequestsYet: 'No request has come in through this key yet.',
  },

  customers: {
    noLogins: 'No logins yet.',
    sendPasswordLink: 'Send a password link',
    accountInfo: 'Account info',
    billingEmail: 'Billing e-mail',
    technicalEmail: 'Technical e-mail',
    wcCustomerId: 'WooCommerce customer ID',
    reverseCharge: 'Reverse charge',
    noOrders: 'No orders.',
    noInstances: 'No instances.',
    searchPlaceholder: 'Search by company or e-mail…',
    terminated: 'Terminated',
    addressLine2Example: 'e.g. c/o, department',
    shownOnInvoice: 'Shown on the invoice',
  },

  instances: {
    open: 'Open',
    address: 'Address',
    noAddress: 'no domain set',
    keySet: '· key',
    selfHostedHint:
      "Runs on the customer's own hardware. SSH access is not guaranteed — administration stays with them.",
    noLink: 'No link.',
    noKeyYet: '· no key yet',
    notFound: 'Instance not found.',
    linkedInstances: 'Linked instances',
    info: 'Instance info',
    noJobs: 'No deployment jobs yet.',
    secretKeys: 'Secrets',
    secretsHint: 'Values are encrypted and never displayed.',
    noSecrets: 'No secrets yet.',
    deleteSecret: 'Delete secret',
    secretValue: 'secret value',
    currentConfig: 'Current configuration',
  },

  baseStacks: {
    slug: 'Slug',
    ports: 'Ports',
    variable: 'Variable',
    origin: 'Origin',
    valueForHost: 'Value for this host',
    checkLabel: 'Check',
    statusActive: 'ACTIVE — selectable',
    suggestionOverridable: 'Suggestion, can be overridden per server',
    statusDraft: 'DRAFT — not offered yet',
    statusDeprecated: 'DEPRECATED — no longer offered',
    externalNetworks: 'External networks',
    ownValueStored: 'A value of its own is stored for this host; the derivation no longer applies.',
    tickedByDefault: 'Ticked by default on new servers',
    stopKeepVolumes: 'Shut the stack down, keep the volumes',
    hostNotReady: 'The host is not ready',
  },

  releases: {
    workerImage: 'Third image (ojfm: Mustang) — empty means: same as backend',
    version: 'Version',
    channel: 'Channel',
    migration: 'Migration',
    breaking: 'Breaking',
    approvedBy: 'Approved by',
    name: 'Release name',
    none: 'No releases found.',
    notes: 'Release notes',
    new: 'New release',
  },

  tenants: {
    enabledModules: 'Enabled modules',
    doNotPreset: 'do not preset',
    displayNameExample: 'Display name, e.g. Edition Halbig',
    codeHint: 'Code — empty: derived from the name',
  },

  users: {
    you: '(you)',
    registryAccounts: 'Registry accounts',
    newUser: 'Create a new user',
    noActivity: 'No activity recorded yet.',
  },

  docker: {
    restart: 'Restart',
    restartsSinceCreation: 'Restarts since the container was created',
    notFromPc: 'Not rolled out through the Provisioning Center',
  },

  orders: {
    setStatus: "Set status…",
    setStatusConfirm: "Set the order status to {{status}}?\n\nThis normally comes from WooCommerce. Change it by hand only when the automatic path did not work.",
  },
  providerCredentials: {
    title: 'Provider access',
    explain:
      'Optional. With the customer\'s own API key we order and manage the machine in their account — contract and invoice stay with them. Without one, our house account applies.',
    noneTitle: 'No access stored',
    noneBody: 'The house account applies. Everything works except what needs the provider API — snapshots above all.',
    add: 'Store key',
    replace: 'Replace',
    tokenLabel: 'API token',
    tokenHint: 'Shown once here, then encrypted. It never appears in a response or in the audit log again.',
    labelField: 'Note (e.g. project name)',
    save: 'Save and check',
    saving: 'Checking…',
    cancel: 'Cancel',
    check: 'Check now',
    checking: 'Checking…',
    checkOk: 'Key works — {{count}} machines visible.',
    remove: 'Remove',
    removeConfirm: 'Remove the stored key for {{provider}}?\n\nIt stays valid at the provider — only the customer can revoke it there.',
    lastUsed: 'last used {{when}}',
    neverUsed: 'never used',
    lastChecked: 'checked {{when}}',
    neverChecked: 'not checked yet',
    scopeHetzner: 'A Hetzner token is scoped to one project. Ask the customer for a token from a project set up for us — we then see nothing else in their account.',
    scopeHostinger: 'A Hostinger token carries the rights of its owner: no projects, no scopes. It is effectively full account access including billing and domains. Say so when asking for it.',
    scopeRobot: 'Robot is a separate interface from the Cloud API, with its own login: a web service user created under Settings → Web service and app settings. Not the account login. Three failed attempts block our IP for ten minutes — for every customer — so do not retry on a hunch.',
    robotUser: 'Web service user',
    robotPassword: 'Password',
  },
  snapshots: {
    title: 'Snapshots',
    explain:
      'Whole machine, taken through the provider API — no SSH involved, so it still works when the machine no longer boots. Crash-consistent, not application-consistent: it does not replace a backup.',
    notPossible: 'Not available for this machine',
    sourceCustomer: 'via the customer\'s account',
    sourceHouse: 'via our house account',
    none: 'No snapshot yet.',
    create: 'Take snapshot',
    creating: 'Starting…',
    descriptionPlaceholder: 'What is this snapshot for?',
    onlyOneWarning: 'This provider keeps exactly one snapshot per machine. A new one discards the existing one.',
    overwriteConfirm: 'Replace the existing snapshot?',
    delete: 'Delete',
    deleteConfirm: 'Delete this snapshot?',
    restore: 'Roll back',
    restoreTitle: 'Roll back to this snapshot',
    restoreWarning: 'This overwrites the whole disk. Every instance on this machine ({{count}}) is rolled back with it.',
    restoreTypeToConfirm: 'Type the number of instances to confirm:',
    restoreGo: 'Roll back now',
    size: '{{gb}} GB',
    expires: 'expires {{when}}',
  },
  robot: {
    title: 'Dedicated machines',
    explain: 'Hetzner AX and EX servers. A different interface from the cloud — and without snapshots, which is a property of the product, not a missing feature here.',
    pickCustomer: 'Whose account?',
    loading: 'Asking Robot…',
    none: 'No dedicated machines in this account.',
    alreadyHere: 'already in the PC',
    cancelled: 'cancelled, runs until {{until}}',
    take: 'Take over',
    taking: 'Taking over…',
    open: 'Take over dedicated machine',
    ownership: 'Belongs to',
    ownershipCustomer: 'the customer',
    ownershipJuni: 'us, dedicated to this customer',
  },
  metrics: {
    title: 'Trends',
    unavailable: 'No graphs available',
    fromHistory: 'hourly values from the history store',
    limit: 'limit',
    since: 'since {{when}}',
    silence: 'Silence',
    silenceHint: 'Silence for 4 hours — it comes back afterwards if still open',
    silencedUntil: 'silent until {{when}}',
    unsilence: 'Unsilence',
    openAlerts_one: '{{count}} open alert',
    openAlerts_other: '{{count}} open alerts',
    open: 'Trends',
    noData: 'No measurements in this period',
    noValue: 'no data',
  },
  instanceStatus: {
    label: 'Status',
    active: 'Running',
    suspended: 'Paused',
    archived: 'Archived',
    terminated: 'Ended',
    activeHint: 'In operation.',
    suspendedHint: 'Temporarily off — comes back.',
    archivedHint: 'Kept for reference, not running, no return.',
    terminatedHint: 'Contract ended.',
    machineState: 'Set by a background job — not editable here.',
    confirmTerminated: 'Set to "Ended"?\n\nThat is the contractual end. For a temporary pause use "Paused", to file it away use "Archived".',
  },
  environment: {
    label: 'Environment',
    production: 'Production',
    staging: 'Stage',
    test: 'Test',
    demo: 'Demo',
    dev: 'Dev',
    unset: 'not set',
    productionHint: 'Runs the customer\'s live operation.',
    stagingHint: 'Acceptance before a release goes live.',
    testHint: 'For trying things out — may break at any time.',
    demoHint: 'Shown to prospects. Not real data.',
    devHint: 'Our own development. Not for customers.',
  },
  images: {
    title: 'Image tags',
    intro: 'Which image each service pulls on the next deploy.',
    service: 'Service',
    tag: 'Tag',
    origin: 'From',
    fromInstance: 'set here',
    fromRelease: 'release',
    fromDefault: 'template default',
    fromOpen: 'not set',
    openHint: 'No tag and no default — the deploy will stop here.',
    reset: 'Back to release',
    resetHint: 'Removes the tag set here. The release applies again.',
    pick: 'Choose a tag',
    custom: 'Type a tag',
    deployNeeded: 'Saved. Takes effect on the next deploy.',
    registryFailed: 'Registry unreachable — type the tag by hand.',
    noImages: 'This instance has no images of ours with a settable tag.',
    built: 'built',
  },
  templateVersion: {
    label: 'Template',
    unset: 'none',
    active: 'current',
    olderHint: 'A newer template exists. Switching takes effect on the next deploy.',
  },
  templateForm: {
    basedOn: 'Prefilled from {{version}} — change what needs changing.',
    nothingToCopy: 'No earlier template for this product. Starting empty.',
    carried: 'Carried over unchanged',
    secrets: 'Generated secrets',
    tenants: 'Tenants',
    adminField: 'Admin address field',
    modules: 'Modules',
    warnEmpty: 'Without these an ojfm instance will not deploy — {{…:?required}} aborts it.',
  },
  releaseImages: {
    title: 'Images',
    fromTemplate: 'Read from template {{version}} — one row per image of ours.',
    none: 'This product has no images of ours with a settable tag.',
    pick: 'Tag',
    empty: 'empty = same as backend',
    noTemplate: 'The product has no template yet.',
  },
  tenantsRead: {
    load: 'Read from the instance',
    loading: 'Reading…',
    hint: 'Fetches the tenants that actually exist there, with their codes. The code is what matters: rename with the right one, create a second tenant with a wrong one.',
    found: '{{count}} found — check, then save.',
    replaced: 'Replaced the list below. Nothing is saved yet.',
  },
  remove: {
    action: 'Remove',
    title: 'Remove instance',
    subtitle: 'Tears down the containers. There is no undo.',
    stepDown: 'docker compose down -v — containers and named volumes go',
    stepDir: 'The instance directory on the host is deleted',
    stepDomain: 'The entry in the domain list is removed',
    stepRecord: 'The record stays, marked DELETED — history and jobs remain readable',
    dataLabel: 'Delete data directories as well',
    dataHint: 'Bind mounts (database, uploads, backups) survive a teardown. Leave this off and they stay — which is what you want if you might come back.',
    dataWarning: 'The database of this instance will be deleted. Only paths carrying the stack name are touched.',
    typeToConfirm: 'To confirm, type',
    confirm: 'Remove',
    running: 'Queueing…',
    failed: 'Removal could not be queued.',
  },
  migrate: {
    action: 'Move',
    title: 'Move to another server',
    subtitle: 'Takes the data along. The instance stands still while it runs.',
    pickTarget: 'Pick a target server',
    noSource: 'no server assigned',
    checking: 'Checking…',
    dataTitle: '{{count}} data items, {{size}} in total',
    noData: 'Nothing found to transfer.',
    unreadable: 'unreadable',
    unreadableWarning: '{{count}} item(s) cannot be read by the SSH user. They would transfer as empty and the job would report success. Fix the permissions first.',
    checksTitle: 'Pre-flight',
    whatHappens: 'The stack is stopped, the data goes through the PC to the target, and the instance is redeployed there. Nothing is deleted on the source — the way back is a start on the old host.',
    blocked: 'A blocking check failed.',
    confirm: 'Move',
    running: 'Queueing…',
    failed: 'The move could not be queued.',
  },
  tenantsSave: {
    done_one: 'Saved — 1 tenant on record.',
    done_other: 'Saved — {{count}} tenants on record.',
    deployNeeded: 'Reaches the instance on the next deploy.',
    retireNote: 'Tenants not on the list will be set inactive there.',
  },
  headline: {
    releaseHint: 'Released version for this instance (channel: {{channel}}).',
    noReleaseHint: 'No release set — this is the tag {{dienst}} will pull ({{herkunft}}).',
  },
  listenSicht: {
    aktiv: 'Active',
    inaktiv: 'Inactive',
    alle: 'All',
  },
  templateCheck: {
    ok_one: 'Checked — 1 service, nothing to flag.',
    ok_other: 'Checked — {{count}} services, nothing to flag.',
    failed: 'The template cannot be saved like this:',
  },
  sql: {
    tab: 'SQL',
    title: 'SQL against this instance’s database',
    intro: 'What would otherwise go through pgAdmin. Every call is logged — who, when, which statement.',
    read: 'Read',
    running: 'Running…',
    readOnlyHint: 'Read-only — Postgres rejects any write.',
    writeMode: 'Allow writes',
    writeWarning: 'A dry run first: it executes the statement, shows how many rows it touches, and rolls back. Writing comes after that, with the stack name typed.',
    dryRun: 'Dry run (rolled back)',
    write: 'Write for real',
    aborted: 'aborted',
    rows_one: '1 row',
    rows_other: '{{count}} rows',
    truncated: 'Truncated — there are more rows than shown.',
    failed: 'The statement could not be run.',
    mode_lesen: 'Read',
    mode_probelauf: 'Dry run — rolled back',
    mode_schreiben: 'Written',
  },
  secrets: {
    doNotRotate: 'do not rotate',
    typeKeyToDelete: 'Type “{{key}}” to delete.',
  },
  dns: {
    label: 'DNS',
    pc: 'by the PC',
    beiwagen: 'sidecar in the stack',
    manuell: 'manual',
    pcHint: 'The PC sets the A record on deploy and after a move. The Cloudflare token stays on the PC.',
    beiwagenHint: 'A DDNS container in the stack refreshes the record every five minutes. Catches a host whose address changes on its own — but needs the token on that host, and its IP must be on the token allowlist.',
    manuellHint: 'Nobody on our side. With a customer domain the customer points at the host.',
  },
  /**
   * Cookieless reach measurement for website instances.
   *
   * The wording carries the limits of the method, and that is deliberate: a
   * number copied into a slide takes its caption along. "Daily visits" is not
   * "visitors", and nothing here may suggest it is.
   */
  /** The running build, bottom left in the sidebar. */
  version: {
    copyHint: 'Copy version number',
    copied: 'copied',
    whatsNew: "What's new?",
    whatsNewWithVersion: "What's new? · v{{version}}",
    changelogEmpty: 'No summary available for this version.',
  },

  webstat: {
    tab: 'Statistics',

    // ─── The limits, stated up front ────────────────────────────────────────
    limitsTitle: 'Cookieless measurement',
    limits:
      'Collected without cookies and without storing anything on the device. A visit is only ' +
      'grouped within one calendar day — after that the same person is no longer recognisable. ' +
      'There are therefore no visitors, no returning visitors and no journeys across visits. ' +
      'All figures are lower bounds: anyone blocking the counter is not counted.',

    // ─── Periods ────────────────────────────────────────────────────────────
    today: 'Today',
    yesterday: 'Yesterday',
    days7: '7 days',
    days28: '28 days',
    days90: '90 days',
    months12: '12 months',
    periodRange: '{{from}} to {{to}}',

    // ─── Key figures ────────────────────────────────────────────────────────
    pageviews: 'Page views',
    visits: 'Daily visits',
    visitsHint: 'Not visitors. Someone coming on three days counts three times.',
    viewsPerVisit: 'Views per visit',
    singlePage: 'One page only',
    singlePageHint: 'of visits',
    duration: 'Time on page',
    durationHint: 'visible time, on average',
    comparedToPrevious: 'compared with the equally long period before',

    notCounted: 'Not counted: {{bots}} bots, {{addresses}} from excluded addresses.',
    notCountedBots: 'Not counted: {{bots}} bots.',
    notCountedAddresses: 'Not counted: {{addresses}} from excluded addresses.',

    // ─── Pages ──────────────────────────────────────────────────────────────
    pages: 'Pages',
    page: 'Page',
    entries: 'Entries',
    exits: 'Exits',
    withAction: '{{count}} w. action',
    withActionHint:
      'Exits where something was clicked beforehand — on a contact page that is the goal, not a loss.',
    readTo: 'Read to',
    scrollHint:
      'Share of measured views reaching 25/50/75/100 % — measured on {{count}} view(s)',
    measuredOn: 'on {{measured}} of {{total}}',
    measuredHint:
      'The duration arrives when the page is left and sometimes gets lost. It is not extrapolated.',
    fitsViewport: 'fits the viewport',
    fitsViewportHint:
      'The page fits in the window, so reading depth is trivially 100 % and not comparable.',
    noPages: 'No page views in this period.',

    // ─── Origin and technology ──────────────────────────────────────────────
    originAndTech: 'Origin and technology',
    referrer: 'Referrer',
    campaign: 'Campaign',
    source: 'Source',
    country: 'Country',
    device: 'Device',
    browser: 'Browser',
    system: 'System',
    value: 'Value',
    noValues: 'Nothing recorded for this attribute in this period.',

    // ─── No figures yet ─────────────────────────────────────────────────────
    noFigures: 'No figures yet',

    // ─── Setup ──────────────────────────────────────────────────────────────
    setup: 'Setup',
    switchedOn: 'Reach measurement is switched on.',
    switchedOff: 'Reach measurement is switched off.',
    switchOn: 'switch on',
    switchOff: 'switch off',
    checkNow: 'check now',
    checking: 'checking…',
    checkNowHint:
      'Fetch the website now and check whether the counter tag and the privacy section are there',
    switchOnBlocked: 'Confirm the privacy section first',
    scriptLine: 'This line goes into the website markup',
    scriptLinePreview: 'This is how the counter will be embedded later',
    scriptLineHint:
      'The key is in the .env of the instance as STAT_ID — the markup takes a placeholder, not a fixed value. That way the same source runs in staging and production. On the error page also set data-fehler="404".',
    privacyText: 'This section goes into the privacy policy of the website',
    confirmPrivacy:
      'The section above will be added to the privacy policy of this website. Without it, measuring is not permitted — the PC checks weekly whether it is there.',
    switchOnFailed: 'Switching on failed.',
    checkFailed: 'Check failed.',
    copy: 'copy',
    copied: 'copied',

    // ─── What the check found ───────────────────────────────────────────────
    checkTitle: 'Check on the website',
    tagMissing:
      'No counter tag found on the start page. Nothing is counted until it is embedded.',
    tagMissingSince:
      'No counter tag found on the start page (for {{days}} days). Nothing is counted until it is embedded.',
    privacyMissing:
      'The privacy policy is missing the section on reach measurement. Without it, measuring should not happen.',
    privacyMissingSince:
      'The privacy policy is missing the section on reach measurement (for {{days}} days). Without it, measuring should not happen.',

    // ─── Sitemap comparison ─────────────────────────────────────────────────
    //
    // The measurements alone only show what was visited. The more useful
    // marketing question is often the other one: which page does nobody see?
    comparison: 'Compared with the sitemap',
    unvisited: 'In the sitemap, never opened',
    unvisitedHint:
      'These pages are meant to be found and were not opened once in this period.',
    unlisted: 'Opened, but not in the sitemap',
    unlistedHint:
      'Either forgotten in the sitemap, or these pages should have disappeared long ago.',
    removedFromSitemap: 'no longer in the sitemap',
    sitemapState: 'sitemap read on {{when}}',
    sitemapNever:
      'The sitemap has not been read yet. Without it the dashboard only knows the pages that were opened.',
    readSitemap: 'read sitemap now',
    reading: 'reading…',
    sitemapResult: '{{count}} pages read, {{added}} new, {{gone}} no longer listed.',
    // ─── Export ─────────────────────────────────────────────────────────────
    exportTitle: 'Export',
    exportHint:
      'Always daily values, regardless of the period shown — one row per day. Any summary can be built from that in a spreadsheet; the other way round it cannot.',
    exportDays: 'Daily totals',
    exportPages: 'Pages per day',
    exportAttributes: 'Origin per day',
    exportComparison: 'Sitemap comparison',
    exportFailed: 'Export failed.',
    noComparison: 'Sitemap and measurements agree — nothing left over on either side.',
    lastChecked: 'last checked: {{when}}',
  },
} as const;

export default en;
